import { motion } from "framer-motion";

const STATS = [
  { value: "10K+", label: "Active Fans" },
  { value: "15K+", label: "Daily Posts" },
  { value: "10+",  label: "Sports" },
];

export default function HeroSection({ onGetApp }) {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* ── Background ─────────────────────────────────────────────── */}
      <div className="absolute inset-0 bg-[#0C0E13]">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0D1F]/70 via-[#0C0E13] to-[#0C0E13]" />

        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.022]"
          style={{
            backgroundImage:
              "linear-gradient(#3B9EFF 1px, transparent 1px), linear-gradient(90deg, #3B9EFF 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

        {/* Centre glow */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#3B9EFF]/7 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute top-2/3 left-1/2 -translate-x-1/2 w-80 h-80 bg-[#3B9EFF]/4 rounded-full blur-[80px] pointer-events-none" />
      </div>

      {/* ── Content ────────────────────────────────────────────────── */}
      <div className="relative w-full max-w-4xl mx-auto px-6 md:px-12 py-24 flex flex-col items-center text-center">

        {/* Label */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="inline-flex items-center gap-2.5 mb-8"
        >
          <span className="relative flex h-2 w-2 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#3B9EFF] opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#3B9EFF]" />
          </span>
          <span className="text-xs uppercase tracking-[0.28em] font-black text-[#3B9EFF] font-outfit">
            Pre-Registration · Now Open on Google Play
          </span>
        </motion.div>

        {/* H1 */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, delay: 0.25 }}
          className="font-outfit font-black tracking-tighter uppercase text-white leading-[0.92] mb-8"
          style={{ fontSize: "clamp(3.5rem, 9vw, 7.5rem)" }}
        >
          <span className="block">All Sports</span>
          <span className="block">
            All <span className="text-[#3B9EFF]">Fans</span>
          </span>
          <span className="block">One Place</span>
        </motion.h1>

        {/* Subtext */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="text-base sm:text-lg leading-relaxed text-[#A1A1AA] font-inter max-w-xl mb-6"
        >
          Join millions of fans. Share your passion, read top sports stories, and discover creator content — all in one app built for true sports fans.
        </motion.p>

        {/* Phase pills */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="flex flex-wrap items-center justify-center gap-2 mb-10"
        >
          {[
            { label: "Community", active: true },
            { label: "Articles",  active: true },
            { label: "Shop · Coming Soon", active: false },
          ].map(({ label, active }) => (
            <span
              key={label}
              className="flex items-center gap-1.5 rounded-full px-3 py-1.5"
              style={{
                background: active ? "rgba(59,158,255,0.10)" : "rgba(255,255,255,0.04)",
                border: active ? "1px solid rgba(59,158,255,0.25)" : "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: active ? "#3B9EFF" : "rgba(255,255,255,0.25)" }}
              />
              <span
                className="text-[10px] font-bold uppercase tracking-[0.2em]"
                style={{ color: active ? "#3B9EFF" : "rgba(255,255,255,0.3)" }}
              >
                {label}
              </span>
            </span>
          ))}
        </motion.div>

        {/* CTA buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.55 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
        >
          <button
            data-testid="hero-playstore-btn"
            onClick={onGetApp}
            className="flex items-center gap-3 bg-[#3B9EFF] text-black font-bold rounded-full px-8 py-4 hover:bg-white hover:scale-[1.04] active:scale-[0.97] transition-all duration-150"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 flex-shrink-0">
              <path d="M3 20.5v-17c0-.83 1.01-1.3 1.63-.74L20 11.5 4.63 21.24C4.01 21.8 3 21.33 3 20.5z" />
            </svg>
            <div className="text-left">
              <div className="text-[10px] font-medium opacity-60 uppercase tracking-wider leading-none mb-0.5">Pre-Register on</div>
              <div className="text-sm font-black uppercase tracking-wide leading-none">Google Play</div>
            </div>
          </button>

          <a
            href="#download"
            data-testid="hero-appstore-btn"
            className="flex items-center gap-3 bg-transparent border border-white/20 text-white font-bold rounded-full px-8 py-4 hover:bg-white/5 hover:border-white/40 hover:scale-[1.04] active:scale-[0.97] transition-all duration-150"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 flex-shrink-0">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
            <div className="text-left">
              <div className="text-[10px] font-medium opacity-60 uppercase tracking-wider leading-none mb-0.5">Coming soon to</div>
              <div className="text-sm font-black uppercase tracking-wide leading-none">App Store</div>
            </div>
          </a>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="flex items-center justify-center gap-10 sm:gap-16 pt-6 border-t border-white/8 w-full max-w-sm"
        >
          {STATS.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 + i * 0.1, duration: 0.5 }}
              className="text-center"
            >
              <div className="text-2xl font-outfit font-black text-[#3B9EFF]">{stat.value}</div>
              <div className="text-xs text-[#A1A1AA] mt-0.5 font-inter">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0C0E13] to-transparent pointer-events-none" />
    </section>
  );
}
